import { useEffect } from 'react';
import { MapContainer, Marker, useMap } from 'react-leaflet';
import L from '@/utils/leaflet-fix';
import { OsmTileLayer } from '@/components/maps/OsmTileLayer';
import { BaseModal } from '@/components/common/BaseModal';
import { useTranslation } from '@/store/languageStore';

export interface TrackerAlarm {
  id: string;
  eventType: string;
  clickKind: 'click1' | 'click2' | 'long' | null;
  latitude: number | null;
  longitude: number | null;
  occurredAt: string;
  imei: string;
  userName: string | null;
  phone: string | null;
}

function FitAlarm({ position }: { position: [number, number] | null }) {
  const map = useMap();
  useEffect(() => {
    if (!position) return;
    map.setView(position, 15);
    const timer = window.setTimeout(() => map.invalidateSize(), 80);
    return () => window.clearTimeout(timer);
  }, [map, position]);
  return null;
}

function alarmIcon() {
  return L.divIcon({
    className: 'personal-tracker-marker',
    html: '<div style="width:18px;height:18px;border-radius:9999px;background:#DC2626;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.45)"></div>',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
}

export default function PersonalTrackerAlarmModal({
  alarms,
  selectedId,
  onSelect,
  onAck,
  acking,
  onClose,
}: {
  alarms: TrackerAlarm[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAck: (id: string) => void;
  acking: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const selected = alarms.find((alarm) => alarm.id === selectedId) ?? alarms[0] ?? null;
  const position = selected && selected.latitude != null && selected.longitude != null
    ? [selected.latitude, selected.longitude] as [number, number]
    : null;

  const kindKey = selected?.clickKind === 'click1'
    ? 'alarmClick1'
    : selected?.clickKind === 'click2'
      ? 'alarmClick2'
      : selected?.clickKind === 'long'
        ? 'alarmLong'
        : null;

  return (
    <BaseModal
      isOpen
      onClose={onClose}
      title={t('personalTrackers.alarmTitle')}
      size="4xl"
      footer={selected ? (
        <button
          type="button"
          className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          disabled={acking}
          onClick={() => onAck(selected.id)}
        >
          {t('personalTrackers.alarmAck')}
        </button>
      ) : undefined}
    >
      {alarms.length === 0 || !selected ? (
        <p className="text-sm text-textSecondary">{t('personalTrackers.alarmEmpty')}</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
          <ul className="max-h-[420px] space-y-1 overflow-y-auto">
            {alarms.map((alarm) => (
              <li key={alarm.id}>
                <button
                  type="button"
                  className={`w-full rounded-md px-2 py-2 text-left text-sm ${alarm.id === selected.id ? 'bg-bgLight' : 'hover:bg-bgLight'}`}
                  onClick={() => onSelect(alarm.id)}
                >
                  <span className="font-medium text-textDefault">
                    {t(`personalTrackers.alarmType.${alarm.eventType}`)}
                  </span>
                  <span className="block text-textSecondary">{alarm.userName || alarm.imei}</span>
                  <span className="block text-xs text-textSecondary">{formatWhen(alarm.occurredAt)}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="space-y-3">
            <div className="text-sm">
              <p className="font-medium text-textDefault">
                {t(`personalTrackers.alarmType.${selected.eventType}`)}
                {kindKey ? ` · ${t(`personalTrackers.${kindKey}`)}` : ''}
              </p>
              <p>{t('personalTrackers.alarmWho')}: {selected.userName || '–'}</p>
              <p>
                {t('personalTrackers.alarmPhone')}: {selected.phone || t('personalTrackers.alarmNoPhone')}
              </p>
              <p className="text-textSecondary">{formatWhen(selected.occurredAt)} · {selected.imei}</p>
            </div>
            <div className="h-[360px] overflow-hidden rounded-lg border border-border">
              {position ? (
                <MapContainer center={position} zoom={15} className="h-full w-full">
                  <OsmTileLayer />
                  <FitAlarm position={position} />
                  <Marker position={position} icon={alarmIcon()} />
                </MapContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-textSecondary">
                  {t('personalTrackers.alarmNoPosition')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </BaseModal>
  );
}
